import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { form, FormField, FormRoot, minLength, required, submit } from '@angular/forms/signals';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';

import { LangToggleComponent } from '@app/shared/components/lang-toggle/lang-toggle.component';
import { passwordsMatch, passwordStrength } from '@app/shared/forms/rules';
import { AuthService } from '@app/shared/services/auth.service';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';

interface ResetPasswordFormModel {
  newPassword: string;
  newPasswordConfirm: string;
}

const emptyModel = (): ResetPasswordFormModel => ({ newPassword: '', newPasswordConfirm: '' });

@Component({
  imports: [
    LangToggleComponent,
    TranslatePipe,
    FormField,
    FormRoot,
    RouterModule,
    UiButtonComponent,
    UiInputComponent,
    UiSpinnerDirective
  ],
  selector: 'proffeo-password',
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnInit {
  private readonly authService: AuthService = inject(AuthService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private userIdParam: string;
  private tokenParam: string;

  protected readonly model = signal<ResetPasswordFormModel>(emptyModel());
  protected readonly form = form(this.model, path => {
    required(path.newPassword, { message: 'ui-forms-this-field-is-required' });
    minLength(path.newPassword, 8, { message: 'password-validation-message' });
    passwordStrength(path.newPassword, { message: 'password-validation-message' });
    required(path.newPasswordConfirm, { message: 'ui-forms-this-field-is-required' });
    passwordsMatch(path.newPasswordConfirm, path.newPassword, { message: 'confirm-password-does-not-match-password' });
  });

  public isLoading: WritableSignal<boolean> = signal<boolean>(true);
  public isLinkValid: WritableSignal<boolean> = signal<boolean>(false);
  public formStatus = signal({
    isSuccess: false,
    isError: false
  });

  public ngOnInit(): void {
    this.setLinkParams();
    void this.validateUserToken();
  }

  private setLinkParams(): void {
    const user = this.route.snapshot.queryParamMap.get('user');
    const token = this.route.snapshot.queryParamMap.get('token');

    if (token) this.tokenParam = token;
    if (user) this.userIdParam = user;
  }

  private async validateUserToken(): Promise<void> {
    const data = {
      userId: this.userIdParam,
      token: this.tokenParam
    };

    const isValid = await this.authService
      .checkResetPasswordToken(data)
      .then(() => true)
      .catch(() => false);
    this.isLinkValid.set(isValid);
    this.isLoading.set(false);
  }

  public onSubmitForm(): void {
    void submit(this.form, {
      action: async () => {
        this.formStatus.set({ isSuccess: false, isError: false });
        try {
          await this.authService.resetPassword({
            userId: this.userIdParam,
            token: this.tokenParam,
            password: this.model().newPassword
          });
          this.model.set(emptyModel());
          this.form().reset();
          this.formStatus.set({ isSuccess: true, isError: false });
        } catch {
          this.formStatus.set({ isSuccess: false, isError: true });
        }
      }
    });
  }
}
